import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import { kinds } from 'Helpers/Props';
import { setTdarrAccessToken, TdarrApiError, tdarrGet, tdarrPost } from '../api';

interface TokenResponse { token: string }
type ProviderId = 'google' | 'microsoft' | 'github' | 'oidc';
const providerNames: Record<ProviderId, string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub', oidc: 'Custom OIDC' };

function errorMessage(error: Error | null) {
  if (error instanceof TdarrApiError) {
    if (typeof error.body === 'string') return error.body;
    if (error.body && typeof error.body === 'object' && 'message' in error.body) return String(error.body.message);
  }
  return error?.message || 'Authentication failed.';
}

export default function Authentication({ setupAdmin }: { setupAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [ssoError, setSsoError] = useState('');
  const sso = useQuery({ queryKey: ['tdarr', 'sso-public'], queryFn: () => tdarrGet<{ providers: ProviderId[] }>('/public/auth/sso'), enabled: !setupAdmin, retry: false });
  const mutation = useMutation({
    mutationFn: () => tdarrPost<TokenResponse>(setupAdmin ? '/public/auth/register' : '/public/auth/login', { username: username.trim(), password }),
    onSuccess: async ({ token }) => {
      setTdarrAccessToken(token);
      await queryClient.invalidateQueries({ queryKey: ['tdarr'] });
    }
  });
  const exchange = useMutation({
    mutationFn: ({ provider, ticket }: { provider: ProviderId; ticket: string }) => tdarrPost<TokenResponse>(`/public/auth/sso/${provider}/exchange`, { ticket }),
    onSuccess: async ({ token }) => { setTdarrAccessToken(token); await queryClient.invalidateQueries({ queryKey: ['tdarr'] }); },
    onError: () => setSsoError('Single sign-on could not be completed.')
  });
  const startSso = useMutation({
    mutationFn: (provider: ProviderId) => tdarrPost<{ authUrl: string }>(`/public/auth/sso/${provider}/login`),
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
    onError: () => setSsoError('Single sign-on could not be started.')
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const provider = params.get('sso-provider') as ProviderId | null;
    const ticket = params.get('sso-ticket');
    const error = params.get('sso-error');
    if (!provider) return;
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
    if (error) setSsoError(`Single sign-on failed: ${error}`);
    else if (ticket) exchange.mutate({ provider, ticket });
  }, []);
  const valid = Boolean(username.trim() && password && (!setupAdmin || (password === confirmation && password.length >= 8)));
  const submit = () => { if (valid && !mutation.isPending) mutation.mutate(); };

  return <PageContent title={setupAdmin ? 'Create Administrator' : 'Sign In'}><PageContentBody>
    <FieldSet legend={setupAdmin ? 'First Run' : 'Authentication Required'}>
      <Alert>{setupAdmin ? 'Create the first Tdarr administrator account. This account receives the admin and user roles.' : 'Sign in with a Tdarr account to continue.'}</Alert>
      <FormGroup><FormLabel name="authUsername">Username</FormLabel><TextInput name="authUsername" value={username} autoFocus={true} onChange={({ value }) => setUsername(String(value))} /></FormGroup>
      <FormGroup><FormLabel name="authPassword">Password</FormLabel><TextInput name="authPassword" type="password" value={password} onChange={({ value }) => setPassword(String(value))} /></FormGroup>
      {setupAdmin && <FormGroup><FormLabel name="authConfirmation">Confirm Password</FormLabel><TextInput name="authConfirmation" type="password" value={confirmation} hasError={Boolean(confirmation && confirmation !== password)} onChange={({ value }) => setConfirmation(String(value))} /></FormGroup>}
      {setupAdmin && password.length > 0 && password.length < 8 && <Alert kind={kinds.WARNING}>Use at least eight characters.</Alert>}
      {mutation.isError && <Alert kind={kinds.DANGER}>{errorMessage(mutation.error)}</Alert>}
      {ssoError && <Alert kind={kinds.DANGER}>{ssoError}</Alert>}
      <Button kind={kinds.PRIMARY} isDisabled={!valid || mutation.isPending} onPress={submit}>{mutation.isPending ? 'Please wait…' : setupAdmin ? 'Create Administrator' : 'Sign In'}</Button>
      {!setupAdmin && Boolean(sso.data?.providers.length) && <FieldSet legend="Single Sign-On">{sso.data?.providers.map((provider) => <React.Fragment key={provider}><Button isDisabled={startSso.isPending || exchange.isPending} onPress={() => startSso.mutate(provider)}>Sign in with {providerNames[provider] ?? provider}</Button>{' '}</React.Fragment>)}</FieldSet>}
    </FieldSet>
  </PageContentBody></PageContent>;
}
